import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Anchor-style links use root-relative paths so they work from any page
  // (e.g. /terms, /privacy, /support). Clicking "Pricing" on /terms will
  // navigate to the landing page and scroll to #pricing.
  const navLinks = [
    { label: "Home", href: "/" },
    { label: "Pricing", href: "/#pricing" },
    { label: "Features", href: "/#features" },
    { label: "Compare", href: "/#compare" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? "bg-space-900/95 backdrop-blur-xl border-b border-white/10"
          : "bg-space-900/80 backdrop-blur-lg"
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo — always navigates to home page */}
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <img src="/voxinity-logo.png?v=8" alt="Voxinity AI" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="/login"
              className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
              data-testid="nav-login"
            >
              Login
            </a>
            <a
              href="/join-beta"
              className="px-4 py-2 rounded-full bg-phosphor-400 text-space-900 text-sm font-medium hover:bg-phosphor-500 transition-colors duration-200 glow-green"
              data-testid="nav-join-beta-cta"
            >
              Join Beta
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white bg-white/10 rounded-lg"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
              style={{ backgroundColor: '#08090d' }}
              data-testid="mobile-menu"
            >
              <div className="py-6 px-2 space-y-2 border-t border-white/10">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="block px-4 py-3 text-base text-white hover:bg-white/10 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
                <div className="pt-4 mt-4 border-t border-white/10 space-y-2">
                  <a
                    href="/login"
                    className="block px-4 py-3 text-base text-white hover:bg-white/10 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login
                  </a>
                  <a
                    href="/join-beta"
                    className="block px-4 py-3 rounded-lg bg-phosphor-400 text-space-900 text-base font-medium text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid="mobile-nav-join-beta-cta"
                  >
                    Join Beta
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Subtle glow line on scroll */}
      {isScrolled && (
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
      )}
    </motion.nav>
  );
};

export default Navigation;
